import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@lib/api";
import { API_ROUTES } from "../constants";
import { ArticlesReadData, QUERY_KEY } from "./useGetArticlesRead";

interface ArticlesReadPayload {
  articleId: number;
}

export const useUpdateArticlesRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ArticlesReadPayload) => {
      // fire and forget
      await apiClient.post(API_ROUTES.articlesRead, payload);
    },
    onMutate: async (payload: ArticlesReadPayload) => {
      // Cancel any outgoing fetches for get articles read
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      // Snapshot the previous value
      const previousArticlesRead =
        queryClient.getQueryData<ArticlesReadPayload>([QUERY_KEY]);

      // Optimistically update state
      // This will update the articles read in the cache with the new values
      // It will not update the server, but it will make the UI feel snappier
      // and give the user immediate feedback
      queryClient.setQueryData([QUERY_KEY], (prevState: ArticlesReadData) => {
        // Prevent duplicate ids.
        if (!prevState || prevState.includes(payload.articleId)) {
          return prevState;
        }
        const updatedState = [...prevState, payload.articleId];
        return updatedState;
      });

      return previousArticlesRead;
    },
    onError: (error: unknown, _, context: ArticlesReadPayload | undefined) => {
      console.error("Failed to update articles read:", error);
      // rollback to previous state
      if (context) {
        queryClient.setQueryData([QUERY_KEY], context);
      }
    },
    // refetch after mutation
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
